import { z } from 'zod';

// Members (/api/users) and possible duplicates (/api/duplicates). Schemas check shape only: the
// email format, protected group names and every database rule stay in the services.
// MemberOperationType and DuplicateStatus mirror the models (the tests compare them).

const Id = z.guid();
const Positive = z.string().regex(/^[1-9]\d*$/, 'must be a whole number from 1');
const Flag = z.enum(['true', 'false', '']);
const SortOrder = z.enum(['ASC', 'DESC', 'asc', 'desc']);

export const MemberOperationType = z.enum([
  'provision',
  'password_reset',
  'email_change',
  'set_active',
  'merge',
  'ibm_sync_update',
  'ibm_deleted',
  'name_change',
  'archive',
  'hard_delete',
  'dedupe',
  'missed_registration',
  'not_duplicate',
  'provision_held',
  'group_change',
]);
export type MemberOperationType = z.infer<typeof MemberOperationType>;

export const DuplicateStatus = z.enum(['open', 'merged', 'not_same']);
export type DuplicateStatus = z.infer<typeof DuplicateStatus>;

export const MemberSortField = z.enum(['createdAt', 'updatedAt', 'firstName', 'lastName', 'email', 'ibmLastLoginAt']);
export type MemberSortField = z.infer<typeof MemberSortField>;

/** No body: the route takes nothing but the path. */
export const MemberEmptyRequest = z.strictObject({});
export type MemberEmptyRequest = z.input<typeof MemberEmptyRequest>;

export const MemberIdParams = z.strictObject({ id: Id });
export type MemberIdParams = z.input<typeof MemberIdParams>;

export const MemberOperationParams = z.strictObject({ id: Id, operationId: Id });
export type MemberOperationParams = z.input<typeof MemberOperationParams>;

export const GroupIdParams = z.strictObject({ groupId: Id });
export type GroupIdParams = z.input<typeof GroupIdParams>;

/** GET /api/users; blank filters mean none. */
export const MemberListQuery = z.strictObject({
  page: Positive.optional(),
  limit: Positive.optional(),
  search: z.string().optional(),
  isActive: Flag.optional(),
  noProfile: Flag.optional(),
  cluster: z.string().optional(),
  careerStage: z.string().optional(),
  sector: z.string().optional(),
  sortBy: MemberSortField.optional(),
  sortOrder: SortOrder.optional(),
});
export type MemberListQuery = z.input<typeof MemberListQuery>;

/** POST /api/users/:id/provision */
export const ProvisionMemberRequest = z.strictObject({ force: z.boolean().optional() });
export type ProvisionMemberRequest = z.input<typeof ProvisionMemberRequest>;

/** POST /api/users/:id/email-change; the service checks the address. */
export const EmailChangeRequest = z.strictObject({
  newEmail: z.string({ error: 'A valid email address is required' }),
});
export type EmailChangeRequest = z.input<typeof EmailChangeRequest>;

/** POST /api/users/active; a result per member. */
export const SetActiveRequest = z.strictObject({
  userIds: z.array(Id).min(1, 'Choose at least one member').max(500, 'At most 500 members at a time'),
  active: z.boolean(),
});
export type SetActiveRequest = z.input<typeof SetActiveRequest>;

/** PUT /api/users/bulk; phoneNumber is the only field that may change. */
export const BulkUpdateMembersRequest = z.strictObject({
  userIds: z.array(Id, { error: 'User IDs array is required' }).min(1, 'User IDs array is required'),
  updates: z
    .strictObject({ phoneNumber: z.string().nullable().optional() })
    .refine((u) => Object.keys(u).length > 0, 'Updates object is required'),
});
export type BulkUpdateMembersRequest = z.input<typeof BulkUpdateMembersRequest>;

/** DELETE /api/users/:id (archive); the service refuses a blank reason. */
export const ArchiveMemberRequest = z.strictObject({ reason: z.string({ error: 'A reason is required' }) });
export type ArchiveMemberRequest = z.input<typeof ArchiveMemberRequest>;

/** POST /api/users/:id/name; the service refuses blank names. */
export const ChangeNameRequest = z.strictObject({
  firstName: z.string({ error: 'First and last name are both required' }),
  lastName: z.string({ error: 'First and last name are both required' }),
});
export type ChangeNameRequest = z.input<typeof ChangeNameRequest>;

/** POST /api/users/groups; the service trims, refuses a blank or taken name. */
export const CreateGroupRequest = z.strictObject({
  name: z.string({ error: 'Group name is required' }),
  description: z.string().nullish(),
});
export type CreateGroupRequest = z.input<typeof CreateGroupRequest>;

/** PATCH /api/users/groups/:groupId; protected groups cannot be renamed (service). */
export const UpdateGroupRequest = z.strictObject({
  name: z.string().optional(),
  description: z.string().nullish(),
});
export type UpdateGroupRequest = z.input<typeof UpdateGroupRequest>;

/** POST /api/users/groups/:groupId/members/by-email; emails is pasted text. A dry run unless dryRun is false. */
export const AddGroupMembersRequest = z.strictObject({
  emails: z.string({ error: 'No email addresses found' }),
  dryRun: z.boolean().optional(),
});
export type AddGroupMembersRequest = z.input<typeof AddGroupMembersRequest>;

/** DELETE /api/users/groups/:groupId/members */
export const RemoveGroupMembersRequest = z.strictObject({
  userIds: z.array(Id, { error: 'User IDs array is required' }).min(1, 'User IDs array is required'),
});
export type RemoveGroupMembersRequest = z.input<typeof RemoveGroupMembersRequest>;

/** GET /api/duplicates; blank means open. */
export const DuplicateListQuery = z.strictObject({
  status: z.enum([...DuplicateStatus.options, 'all', ''], { error: 'Unknown status' }).optional(),
});
export type DuplicateListQuery = z.input<typeof DuplicateListQuery>;

export const DuplicateIdParams = z.strictObject({ id: Id });
export type DuplicateIdParams = z.input<typeof DuplicateIdParams>;

/** POST /api/duplicates/:id/merge; no keepId keeps the suggested record. It must be one of the pair (service). */
export const MergeDuplicateRequest = z.strictObject({ keepId: z.union([Id, z.literal('')]).nullish() });
export type MergeDuplicateRequest = z.input<typeof MergeDuplicateRequest>;
